/**
 * Sprint T-16.1 · Automation Center · JobsTab.
 *
 * Grid of 12 JobCard with search + category/status filters.
 * Actions (Run / Pause / Resume / Detail) delegated to child dialogs.
 */
import React, { useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import { toast } from "sonner";
import { WidgetCard, EmptyState, LoadingState, CitationBadge } from "../advisor";
import { useAutomationJobs, useRunJob, usePauseJob, useResumeJob } from "../../hooks/useAutomation";
import JobCard from "./shared/JobCard";
import { RunJobDialog, PauseJobDialog } from "./shared/Dialogs";
import { JobDetailPanel } from "./shared/DetailPanels";

const CATEGORIES = [
  { key: "intelligence", label: "IA" },
  { key: "reporting", label: "Reporting" },
  { key: "system", label: "Sistema" },
  { key: "marketplace", label: "Marketplace" },
  { key: "compliance", label: "Compliance" },
];

const STATUSES = [
  { key: "success", label: "OK" },
  { key: "failed", label: "Fail" },
  { key: "dead", label: "Dead" },
  { key: "pending", label: "Pending" },
  { key: "paused", label: "Paused" },
  { key: "never-run", label: "Nunca ejecutado" },
];

function _matchesStatus(item, statusKey) {
  const st = item.state || {};
  if (statusKey === "never-run") return !st.last_status;
  if (statusKey === "paused") {
    if (!st.paused_until) return false;
    try {
      return new Date(st.paused_until).getTime() > Date.now();
    } catch {
      return false;
    }
  }
  return st.last_status === statusKey;
}


export default function JobsTab() {
  const q = useAutomationJobs();
  const run = useRunJob();
  const pause = usePauseJob();
  const resume = useResumeJob();

  const [search, setSearch] = useState("");
  const [cats, setCats] = useState(new Set());
  const [statuses, setStatuses] = useState(new Set());
  const [runFor, setRunFor] = useState(null);
  const [pauseFor, setPauseFor] = useState(null);
  const [detailFor, setDetailFor] = useState(null);

  const toggle = (setFn, key) =>
    setFn((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const items = useMemo(() => {
    const all = q.data?.items || [];
    const s = search.trim().toLowerCase();
    return all.filter((j) => {
      if (cats.size > 0 && !cats.has(j.category)) return false;
      if (statuses.size > 0) {
        const anyMatch = [...statuses].some((k) => _matchesStatus(j, k));
        if (!anyMatch) return false;
      }
      if (s) {
        const hay = `${j.job_id} ${j.name} ${j.description}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [q.data, search, cats, statuses]);

  const doRun = async ({ input, idempotency_key }) => {
    try {
      const res = await run.mutateAsync({ jobId: runFor.job_id, input, idempotency_key });
      toast.success(`Ejecución encolada · ${res.run_id?.slice(0, 8)}`);
      setRunFor(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al ejecutar");
    }
  };

  const doPause = async ({ until }) => {
    try {
      await pause.mutateAsync({ jobId: pauseFor.job_id, until });
      toast.success("Job pausado");
      setPauseFor(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al pausar");
    }
  };

  const doResume = async (job) => {
    try {
      await resume.mutateAsync({ jobId: job.job_id });
      toast.success("Job reanudado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al reanudar");
    }
  };

  return (
    <section className="space-y-4" data-testid="automation-jobs-tab">
      <WidgetCard
        title={`Catálogo · ${items.length} de ${q.data?.count || 0}`}
        description="Filtrar por categoría, estado o búsqueda libre · click en un job para ver detalle."
        citations={["/api/automation/jobs"]}
        testId="widget-jobs-catalog"
      >
        <div className="mb-3 space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2 h-3 w-3 text-[var(--ag-text-dim)]" />
              <input
                data-testid="jobs-search-input"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por job_id / name / description"
                className="w-full rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] py-1.5 pl-7 pr-2 text-xs"
              />
              {search && (
                <button
                  type="button"
                  data-testid="jobs-search-clear"
                  onClick={() => setSearch("")}
                  className="absolute right-1 top-1.5 rounded p-0.5 hover:bg-[var(--ag-surface-2)]"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-1" data-testid="jobs-filter-categories">
            <span className="text-[9px] uppercase text-[var(--ag-text-dim)]">Categoría:</span>
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                type="button"
                data-testid={`jobs-filter-cat-${c.key}`}
                onClick={() => toggle(setCats, c.key)}
                className={`rounded border px-1.5 py-0.5 text-[10px] ${
                  cats.has(c.key)
                    ? "border-[var(--ag-gold)] bg-[var(--ag-gold)]/15 text-[var(--ag-gold)]"
                    : "border-[var(--ag-border)] hover:bg-[var(--ag-surface-2)]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1" data-testid="jobs-filter-statuses">
            <span className="text-[9px] uppercase text-[var(--ag-text-dim)]">Estado:</span>
            {STATUSES.map((s) => (
              <button
                key={s.key}
                type="button"
                data-testid={`jobs-filter-status-${s.key}`}
                onClick={() => toggle(setStatuses, s.key)}
                className={`rounded border px-1.5 py-0.5 text-[10px] ${
                  statuses.has(s.key)
                    ? "border-[var(--ag-gold)] bg-[var(--ag-gold)]/15 text-[var(--ag-gold)]"
                    : "border-[var(--ag-border)] hover:bg-[var(--ag-surface-2)]"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {q.isLoading && <LoadingState />}
        {q.error && <EmptyState message="No se pudo cargar el catálogo." testId="jobs-error" />}
        {q.data && items.length === 0 && (
          <EmptyState
            title="Sin resultados"
            message="Ajusta los filtros o limpia la búsqueda."
            testId="jobs-empty"
          />
        )}
        {items.length > 0 && (
          <div
            className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
            data-testid="jobs-grid"
          >
            {items.map((job) => (
              <JobCard
                key={job.job_id}
                job={job}
                onRun={setRunFor}
                onPause={setPauseFor}
                onResume={doResume}
                onDetail={(j) => setDetailFor(j.job_id)}
              />
            ))}
          </div>
        )}
      </WidgetCard>

      {runFor && (
        <RunJobDialog job={runFor} onClose={() => setRunFor(null)} onConfirm={doRun} pending={run.isPending} />
      )}
      {pauseFor && (
        <PauseJobDialog job={pauseFor} onClose={() => setPauseFor(null)} onConfirm={doPause} pending={pause.isPending} />
      )}
      {detailFor && (
        <JobDetailPanel jobId={detailFor} onClose={() => setDetailFor(null)} />
      )}

      <footer className="border-t border-[var(--ag-border)] pt-3">
        <CitationBadge sources={["1 endpoint = 1 widget", "handlers thin", "ai_platform_freeze=true"]} />
      </footer>
    </section>
  );
}
